import { BASE_URL, extractErrorBody } from './api-client';
import { ApiError } from './errors';

/**
 * Download do arquivo de exportação (SPEC-023 §8).
 *
 * Segunda — e única outra — porta de saída HTTP da SPA. `apiRequest` não serve
 * aqui porque sempre lê a resposta como JSON, e esta rota devolve um arquivo.
 *
 * Por que não um `<a href="/api/v1/exports/…/download">`: o link carregaria o
 * cookie e funcionaria no caminho feliz, mas em `EXPORT_NOT_READY` (409) ou
 * `EXPORT_EXPIRED` (410) o navegador abriria uma aba com o envelope de erro em
 * JSON — exatamente o que o RF-F17 proíbe. Passando pelo `fetch`, todo erro
 * continua saindo pelo catálogo.
 */

/** O nome que a API monta (`downloadFileName` no domínio). */
const EXPECTED_FILE_NAME = /^resultados-\d{4}-\d{2}-\d{2}-[0-9a-f]{8}\.(?:csv|json)$/;

const EXTENSION_BY_CONTENT_TYPE: Record<string, string> = {
  'text/csv': 'csv',
  'application/json': 'json',
};

export async function downloadExport(exportId: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}/exports/${exportId}/download`, {
      method: 'GET',
      credentials: 'same-origin',
    });
  } catch {
    throw new ApiError(0, { code: 'NETWORK_ERROR' });
  }

  if (!response.ok) {
    throw new ApiError(response.status, extractErrorBody(await readJson(response)));
  }

  saveBlob(await response.blob(), fileNameFor(response, exportId));
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * Usa o nome do `Content-Disposition` apenas se ele casar com o padrão
 * esperado (RNF-EU08): o cabeçalho é dado que vem do servidor e vira nome de
 * arquivo no disco de quem baixa.
 */
function fileNameFor(response: Response, exportId: string): string {
  const header = response.headers.get('content-disposition');
  const match = header?.match(/filename="([^"]+)"/);
  if (match?.[1] && EXPECTED_FILE_NAME.test(match[1])) return match[1];

  const contentType = response.headers.get('content-type')?.split(';')[0]?.trim() ?? '';
  const extension = EXTENSION_BY_CONTENT_TYPE[contentType] ?? 'txt';
  return `exportacao-${exportId.slice(0, 8)}.${extension}`;
}

function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  // Revogar no próximo tick, não agora: revogar antes de o navegador iniciar o
  // salvamento cancela o download em alguns navegadores. Não revogar nunca
  // seguraria o arquivo inteiro na memória da aba (RNF-EU07).
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
